import * as React from 'react';
import { GoogleGenAI, Chat } from '@google/genai';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { ChatBubbleIcon, XIcon, PaperAirplaneIcon, SpinnerIcon, SparklesIcon } from './common/Icons';
import type { ChatMessage } from '@/shared/types';
import { createLogger } from '@/services/loggingService';

const logger = createLogger('Chatbot');

export const Chatbot: React.FC = () => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [chat, setChat] = React.useState<Chat | null>(null);
    const [messages, setMessages] = React.useState<ChatMessage[]>([]);
    const [input, setInput] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const messagesEndRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const initChat = async () => {
            try {
                const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY;
                if (!geminiApiKey) {
                    setError("Chat feature is not available. Please configure the API key.");
                    return;
                }
                const ai = new GoogleGenAI({ apiKey: geminiApiKey });
                const systemInstruction = `You are a friendly and helpful expert assistant for the 'Amazon Profit Studio' application. Your primary goal is to help users understand and use the tool effectively.

**Key Application Information (Your Knowledge Base):**

*   **Core Purpose:** The tool's goal is to maximize a user's **Total Amazon Profit Dollars**, not just ROAS. It does this by finding the optimal, most profitable ad spend for each individual product (ASIN).
*   **How it Works:**
    1.  It models a unique "sales response curve" for each ASIN from the user's historical data to understand diminishing returns.
    2.  It calculates the precise spend level where incremental ad revenue equals the breakeven point.
    3.  It generates a clear action plan (Budget Reallocation Plan) showing where to cut and increase spend.
*   **Crucial Privacy Feature:** You MUST emphasize that this is a **secure, private, client-side application**.
    *   The user **DOES NOT** connect their Amazon Seller Central or Advertising accounts.
    *   All data is processed **locally in the user's browser** from the CSV file they upload.
    *   No sensitive advertising data is ever uploaded to a server.
*   **User Workflow:**
    1.  **Upload Data:** The user needs to get an "Advertised Product Report" from Amazon Ads (Sponsored Products > Advertised product, Weekly, 60-90 days), rename the columns to \`asin\`, \`date\`, \`spend\`, \`sales\`, and upload it.
    2.  **Set Margins:** The user must provide a "Contribution Margin" for their products. This is critical for accuracy. Contribution Margin % = (Sale Price - Variable Costs) / Sale Price. Ad spend is NOT included in variable costs.
    3.  **Analyze & Implement:** The user runs the analysis and then uses the results to manually adjust budgets in their Amazon Ads console.
*   **Key Features to Mention:**
    *   **Optimizer Tab:** The main analysis area.
    *   **Verification Tab:** For tracking performance after making changes by uploading a new 1-week report.
    *   **Portfolio Health Tab:** Shows high-level metrics like "Zombie ASINs" (spend with no sales) and spend concentration.
    *   **AI-Powered Summary:** A feature that uses the Gemini API to create an executive summary of the results.

**Your Persona & Rules:**
*   Be concise, clear, and encouraging.
*   When a user asks how to start or what the app does, explain the CSV upload process and the privacy features.
*   You can explain concepts like "Incremental ROAS" or "Contribution Margin" based on the definitions in the app's glossary.
*   **DO NOT** provide financial advice. Your role is to explain how the tool works and what its outputs mean.
*   If a question is ambiguous, ask for clarification.`;
                
                const chatSession = ai.chats.create({
                    model: 'gemini-2.5-flash',
                    config: {
                        systemInstruction: systemInstruction,
                    },
                });
                setChat(chatSession);
                setMessages([
                    { role: 'model', content: "Hello! I'm an AI assistant. How can I help you with the Amazon Ads Optimizer or your advertising questions today?" }
                ]);
            } catch (e: unknown) {
                logger.error('Failed to initialize chat', { error: e instanceof Error ? e.message : String(e) });
                setError("Could not start the chat session. Please try again later.");
            }
        };
        initChat();
    }, []);

    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isLoading || !chat) return;

        const userMessage: ChatMessage = { role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        setError(null);

        try {
            const response = await chat.sendMessage({ message: userMessage.content });
            
            const modelResponseText = response.text ?? "Sorry, I couldn't generate a response.";

            const unsafeHtml = await marked.parse(modelResponseText, { breaks: true });
            const sanitizedHtml = DOMPurify.sanitize(unsafeHtml);

            const modelMessage: ChatMessage = { role: 'model', content: sanitizedHtml };
            setMessages(prev => [...prev, modelMessage]);
        } catch (err: unknown) {
            logger.error('Gemini API call failed', { error: err instanceof Error ? err.message : String(err) });
            const errorMessage = "Sorry, I'm having trouble connecting. Please try again in a moment.";
            setError(errorMessage);
            setMessages(prev => [...prev, { role: 'model', content: errorMessage }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Chat Bubble Button */}
            <div className="fixed bottom-6 right-6 z-50">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full p-4 shadow-lg hover:from-cyan-400 hover:to-blue-400 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-[#0a0a0f]"
                    aria-label={isOpen ? "Close chat" : "Open chat"}
                >
                    {isOpen ? <XIcon className="h-6 w-6" /> : <ChatBubbleIcon className="h-6 w-6" />}
                </button>
            </div>

            {/* Chat Window */}
            <div className={`chatbot-window ${isOpen ? 'open' : ''}`}>
                <div className="flex flex-col h-full border border-white/10 bg-[#0f1015]/95 shadow-xl rounded-xl backdrop-blur-xl">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5 rounded-t-xl">
                        <div className="flex items-center gap-2">
                             <SparklesIcon className="h-5 w-5 text-cyan-400" />
                            <h3 className="text-lg font-semibold text-white">AI Assistant</h3>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-slate-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
                            aria-label="Close chat"
                        >
                            <XIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-grow p-4 overflow-y-auto chatbot-messages">
                        <div className="space-y-4">
                            {messages.map((msg, index) => (
                                <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-xs md:max-w-md lg:max-w-lg px-4 py-2 rounded-2xl ${
                                        msg.role === 'user'
                                            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-br-lg'
                                            : 'bg-white/10 text-slate-200 rounded-bl-lg'
                                    }`}>
                                        <div
                                            className="prose prose-sm prose-invert max-w-none prose-p:my-2"
                                            dangerouslySetInnerHTML={{ __html: msg.content }}
                                        />
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <div className="flex justify-start">
                                    <div className="px-4 py-2 rounded-2xl bg-white/10 rounded-bl-lg">
                                        <div className="flex items-center gap-2 text-slate-400">
                                            <SpinnerIcon className="h-5 w-5 text-cyan-400 animate-spin" />
                                            <span>Thinking...</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-white/10 bg-white/5 rounded-b-xl">
                         {error && !isLoading && <p className="text-xs text-rose-400 mb-2">{error}</p>}
                        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask a question..."
                                className="flex-grow px-3 py-2.5 min-h-[44px] rounded-xl border border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 sm:text-sm disabled:bg-white/5 disabled:opacity-50"
                                disabled={isLoading || !chat}
                            />
                            <button
                                type="submit"
                                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-3 rounded-xl hover:from-cyan-400 hover:to-blue-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[44px] flex items-center justify-center"
                                disabled={isLoading || !input.trim() || !chat}
                                aria-label="Send message"
                            >
                                <PaperAirplaneIcon />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};